'use client';

import React, { createContext, useContext } from 'react';

export interface TenantBranding {
  logoUrl: string | null;
  faviconUrl: string | null;
  /** Website primary brand color. Not used by the native app. */
  primaryColor: string;
  /** Website secondary brand color. Not used by the native app. */
  secondaryColor: string;
  /** Native app page background. Source of truth for `useNativeTokens().background`. */
  appBackgroundColor?: string;
  /** Native app text/ink color. Source of truth for `useNativeTokens().foreground`. */
  appTextColor?: string;
  /** Per-tenant geometry/typography for the native app (`theme_config.native_design`). */
  nativeDesign?: unknown;
  accountName: string;
  accountSlug: string;
}

const TenantBrandingContext = createContext<TenantBranding | null>(null);

/**
 * Provides tenant branding data to child components via context.
 * CSS variables for colors are now injected server-side via inline styles
 * on the parent wrapper element to prevent FOUC (Flash of Unstyled Content).
 */
export function TenantBrandingProvider({
  children,
  branding,
}: {
  children: React.ReactNode;
  branding: TenantBranding;
}) {
  return (
    <TenantBrandingContext.Provider value={branding}>
      {children}
    </TenantBrandingContext.Provider>
  );
}

export function useTenantBranding() {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    throw new Error('useTenantBranding must be used within TenantBrandingProvider');
  }
  return context;
}

export function useTenantBrandingOptional() {
  return useContext(TenantBrandingContext);
}
